import { createContext, useCallback, useContext, useMemo } from 'react';
import { Step } from '@/components/navigation/navigation-stepper';
import { Outlet, useLocation, useParams, useSearchParams } from '@/util/react-router-dom';
import { useTranslation } from '@/contexts/i18n-context';
import { useGetEncounters } from '@/api/get-encounter';
import { EncounterStatusIdsType } from '@/api/api.schemas';
import { useReferenceData } from '../reference-data-context';
import { useGetEncountersWorkflow } from '@/api/get-encounter-workflow';
import { formatVisitSteps } from './step-formatters';
import { useBranding } from '@/outlets/branding-outlet';

export const VisitNavigationContext = createContext<{
  steps: Step[];
  hasCompletedStep: (statusId: EncounterStatusIdsType) => boolean;
  nextStep: Step | undefined;
  prevStep: Step | undefined;
  currentStepStatusId: EncounterStatusIdsType | undefined;
  editMode: string | null;
} | null>(null);

export function VisitNavigationOutlet() {
  const { t } = useTranslation();
  const { visitId } = useParams();
  const { data: encounterResponse } = useGetEncounters(Number(visitId));
  const { data: encounterWorkflow } = useGetEncountersWorkflow(Number(visitId));
  const { pathname } = useLocation();
  const { hasInsurance, hasIdVerification } = useBranding();
  const { data: referenceData } = useReferenceData();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get('editMode');

  const hasCompletedStep = useCallback(
    (statusId: EncounterStatusIdsType): boolean => {
      if (!encounterResponse?.encounter.statusId) {
        return false;
      }
      return (
        referenceData.visitFlow[statusId] <
        referenceData.visitFlow[encounterResponse.encounter.statusId]
      );
    },
    [encounterResponse?.encounter.statusId, referenceData],
  );

  const steps = useMemo(() => {
    return formatVisitSteps({
      workflow: encounterWorkflow,
      pathname,
      encounter: encounterResponse?.encounter,
      hasCompletedStep,
      hasInsurance,
      hasIdVerification,
      t,
    });
  }, [
    encounterWorkflow,
    hasInsurance,
    pathname,
    encounterResponse?.encounter,
    hasCompletedStep,
    hasIdVerification,
    t,
  ]);

  const { nextStep, prevStep } = useMemo(() => {
    const leafSteps = getLeafSteps(steps);
    const activeIndex = leafSteps.findIndex((s) => s.isActive);

    return {
      nextStep: getNavigatableLeafStep(leafSteps, activeIndex + 1, 1),
      prevStep: getNavigatableLeafStep(leafSteps, activeIndex - 1, -1),
    };
  }, [steps]);

  const currentStepStatusId = useMemo(() => {
    const leafSteps = getLeafSteps(steps);
    const activeLeafStep = leafSteps.find((s) => s.isActive);
    return activeLeafStep?.statusId;
  }, [steps]);

  return (
    <VisitNavigationContext.Provider
      value={{ steps, hasCompletedStep, nextStep, prevStep, currentStepStatusId, editMode }}
    >
      <Outlet />
    </VisitNavigationContext.Provider>
  );
}

function getLeafSteps(steps: readonly Step[]): Step[] {
  const leaves: Step[] = [];
  steps.forEach((step) => {
    if (step.children) {
      leaves.push(...getLeafSteps(step.children));
    } else {
      leaves.push(step);
    }
  });
  return leaves;
}

export function useVisitNavigation() {
  const context = useContext(VisitNavigationContext);
  if (!context) {
    throw new Error('useVisitNavigation must be used within a VisitNavigationContext');
  }

  return context;
}

function getNavigatableLeafStep(leafSteps: Step[], index: number, step: 1 | -1) {
  if (leafSteps[index]?.nonNavigable) {
    return getNavigatableLeafStep(leafSteps, index + step, step);
  }

  return leafSteps[index];
}
